import { useMemo, useState } from 'react'
import type { Cell, QueryResultSet } from '@/lib/api/models'
import { isNumericValue, toNumber } from '@/lib/format/cells'

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  columnIndex: number
  direction: SortDirection
}

export interface ResultTableModel {
  /** Row indices in display order, so the original row objects stay untouched. */
  rows: Cell[][]
  numericColumns: boolean[]
  sort: SortState | null
  toggleSort: (columnIndex: number) => void
  clearSort: () => void
}

/**
 * Client-side sort over an already-materialised result set.
 *
 * The backend caps a response at MAX_RESULT_ROWS, so this only ever reorders a
 * page of rows — it is not, and must not be read as, a re-query. When a result
 * is truncated the UI says so next to the sort affordance.
 */
export function useResultTable(result: QueryResultSet): ResultTableModel {
  const [sort, setSort] = useState<SortState | null>(null)

  const numericColumns = useMemo(
    () =>
      result.columns.map((_, index) => {
        const values = result.rows
          .map((row) => row[index])
          .filter((value): value is Cell => value !== null && value !== '')

        return values.length > 0 && values.every(isNumericValue)
      }),
    [result.columns, result.rows],
  )

  const rows = useMemo(() => {
    if (!sort) return result.rows

    const { columnIndex, direction } = sort
    const numeric = numericColumns[columnIndex] ?? false
    const factor = direction === 'asc' ? 1 : -1

    return [...result.rows].sort((left, right) => {
      const a = left[columnIndex] ?? null
      const b = right[columnIndex] ?? null

      // Nulls sort last in both directions — an empty cell is never "the top".
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1

      if (numeric) {
        return ((toNumber(a) ?? 0) - (toNumber(b) ?? 0)) * factor
      }

      return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * factor
    })
  }, [numericColumns, result.rows, sort])

  const toggleSort = (columnIndex: number) => {
    setSort((current) => {
      if (!current || current.columnIndex !== columnIndex) {
        return { columnIndex, direction: 'asc' }
      }
      // asc -> desc -> back to the server's own ordering.
      if (current.direction === 'asc') return { columnIndex, direction: 'desc' }
      return null
    })
  }

  return { rows, numericColumns, sort, toggleSort, clearSort: () => setSort(null) }
}
